import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge } from "reactstrap";

const SubscriptionBadgeModal = () => {
  const [subData, setSubData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("firmSubscription");
      if (stored) {
        setSubData(JSON.parse(stored));
      } else {
        const authUser = localStorage.getItem("authUser");
        if (authUser) {
          const parsed = JSON.parse(authUser);
          if (parsed.FirmName || parsed.PlanName) {
            setSubData({
              firmName: parsed.FirmName || "Firm",
              planName: parsed.PlanName || "Standard",
              planType: parsed.PlanType || "TRIAL",
              startDate: parsed.StartDate,
              endDate: parsed.EndDate,
              daysRemaining: parsed.DaysRemaining !== undefined ? parsed.DaysRemaining : 7,
              graceDaysRemaining: parsed.GraceDaysRemaining || 0,
              subscriptionStatus: parsed.SubscriptionStatus || 100,
              supportContactNumber: parsed.SupportContactNumber || "+91 99999-99999",
            });
          }
        }
      }
    } catch (e) {
      console.warn("Error parsing subscription data:", e);
    }
  }, []);

  if (!subData) return null;

  const toggle = () => setIsOpen(!isOpen);

  const daysLeft = subData.daysRemaining !== undefined ? Number(subData.daysRemaining) : 0;
  const graceDaysLeft = subData.graceDaysRemaining !== undefined ? Number(subData.graceDaysRemaining) : 0;
  const isGrace = subData.subscriptionStatus === 101 || (daysLeft <= 0 && graceDaysLeft > 0);
  const isTrial = (subData.planType || "").toUpperCase() === "TRIAL";
  const supportNo = subData.supportContactNumber || "+91 99999-99999";
  const cleanPhone = supportNo.replace(/\D/g, "");

  const formatDate = (dt) => {
    if (!dt) return "N/A";
    try {
      return new Date(dt).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dt;
    }
  };

  return (
    <React.Fragment>
      {/* Topbar Pill / Badge */}
      <div className="d-flex align-items-center ms-2 me-2">
        <button
          type="button"
          onClick={toggle}
          className="btn btn-sm shadow-sm d-flex align-items-center gap-2"
          style={{
            background: isGrace
              ? "linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)"
              : isTrial
              ? "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)"
              : "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            color: isTrial ? "#212529" : "#ffffff",
            borderRadius: "20px",
            padding: "5px 14px",
            fontWeight: "600",
            fontSize: "13px",
            border: "none",
            transition: "all 0.3s ease",
            cursor: "pointer",
          }}
          title="Click to view Subscription Details & Helpline"
        >
          {isGrace ? (
            <>
              <i className="fas fa-exclamation-triangle"></i>
              <span>Grace Period: {graceDaysLeft} Days Left</span>
            </>
          ) : isTrial ? (
            <>
              <i className="fas fa-gift"></i>
              <span>Free Trial: {daysLeft} Days Left</span>
            </>
          ) : (
            <>
              <i className="fas fa-crown"></i>
              <span>{subData.planName || "Pro Plan"}: {daysLeft} Days</span>
            </>
          )}
        </button>
      </div>

      {/* Subscription Details & Helpline Modal */}
      <Modal isOpen={isOpen} toggle={toggle} centered size="md">
        <ModalHeader
          toggle={toggle}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#ffffff",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-shield-alt font-size-18"></i>
            <span className="fw-bold">My Firm Subscription & Plan</span>
          </div>
        </ModalHeader>
        <ModalBody className="p-4">
          <div className="text-center mb-4">
            <h4 className="fw-bold text-primary mb-1">{subData.firmName || "My Firm"}</h4>
            <Badge
              color={isGrace ? "danger" : isTrial ? "warning" : "success"}
              className="px-3 py-2 fs-6 rounded-pill"
            >
              {isGrace ? "Grace Period (Buffer Active)" : isTrial ? "7-Day Free Trial" : subData.planName}
            </Badge>
          </div>

          <div className="bg-light p-3 rounded-3 mb-3 border">
            <div className="d-flex justify-content-between py-1 border-bottom">
              <span className="text-muted fw-semibold">Current Plan:</span>
              <span className="fw-bold text-dark">{subData.planName || "Standard Plan"}</span>
            </div>
            <div className="d-flex justify-content-between py-1 border-bottom">
              <span className="text-muted fw-semibold">Valid From:</span>
              <span className="fw-semibold">{formatDate(subData.startDate)}</span>
            </div>
            <div className="d-flex justify-content-between py-1 border-bottom">
              <span className="text-muted fw-semibold">Valid Till:</span>
              <span className="fw-semibold">{formatDate(subData.endDate)}</span>
            </div>
            {subData.graceEndDate && (
              <div className="d-flex justify-content-between py-1 border-bottom">
                <span className="text-muted fw-semibold">Buffer (Grace) Till:</span>
                <span className="fw-semibold text-danger">{formatDate(subData.graceEndDate)}</span>
              </div>
            )}
            <div className="d-flex justify-content-between py-1">
              <span className="text-muted fw-semibold">Remaining Days:</span>
              <span className={`fw-bold ${daysLeft <= 3 ? "text-danger" : "text-success"}`}>
                {daysLeft > 0 ? `${daysLeft} Days Left` : "Expired (Grace Period Active)"}
              </span>
            </div>
          </div>

          {/* Upgrade / Renewal Notice */}
          <div
            className="p-3 rounded-3"
            style={{
              background: "#e8f4fd",
              border: "1px solid #b8daff",
            }}
          >
            <h6 className="fw-bold text-primary mb-2">
              <i className="fas fa-headset me-2"></i>Subscription Renewal or Upgrade:
            </h6>
            <p className="text-muted font-size-13 mb-3">
              To renew your license, upgrade your plan, or adjust limits, please reach out to our support helpline:
            </p>

            <div className="d-flex flex-column flex-sm-row gap-2">
              <a
                href={`tel:${cleanPhone}`}
                className="btn btn-primary d-flex align-items-center justify-content-center gap-2 flex-grow-1 py-2 fw-semibold"
                style={{ borderRadius: "8px" }}
              >
                <i className="fas fa-phone-alt"></i> Call: {supportNo}
              </a>
              <a
                href={`https://wa.me/${cleanPhone}?text=${encodeURIComponent(
                  `Hello, I would like to renew or upgrade the subscription for my firm (${subData.firmName}).`
                )}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-success d-flex align-items-center justify-content-center gap-2 flex-grow-1 py-2 fw-semibold"
                style={{ borderRadius: "8px" }}
              >
                <i className="fab fa-whatsapp font-size-16"></i> WhatsApp
              </a>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} style={{ borderRadius: "8px" }}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SubscriptionBadgeModal;
